import cloneDeep from "lodash/cloneDeep"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import InvalidVariable from "./InvalidVariable"
import VariableSelectorList from "./VariableSelectorList"
import VariableSelectorPicker from "./VariableSelectorPicker"

// Insert variable string into delimiters.
function variableTemplate(delimiters, value) {
  const [firstDelimiters, lastDelimiters = ""] = delimiters
  return `${firstDelimiters}${value}${lastDelimiters}`
}

// TODO: handle new line?

export const delimiterConfigsMap = {
  // Current @ delimiter:
  at: {
    key: "at",
    beforeSelectionRe: new RegExp("(?<full>@(?<partial>[a-zA-Z._ ]*) *)$"),
    afterSelectionRe: new RegExp("^(?<full>(?<partial>[a-zA-Z._]*))"),
    template: (value) => variableTemplate`@${value.trim()}`
  },
  slash: {
    key: "slash",
    beforeSelectionRe: new RegExp("(?<full>\\/(?<partial>[a-zA-Z._ ]*) *)$"),
    afterSelectionRe: new RegExp("^(?<full>(?<partial>[a-zA-Z._]*))"),
    template: (value) => variableTemplate`/${value.trim()}`
  },
  // Previous {{ }} delimiter:
  bracket: {
    key: "bracket",
    beforeSelectionRe: new RegExp("(?<full>\\{\\{ *(?<partial>[a-zA-Z._ ]*) *)$"), // prettier-ignore
    afterSelectionRe: new RegExp("^(?<full> *(?<partial>[a-zA-Z._ ]*) *\\}\\})"), // prettier-ignore
    // template: value => variableTemplate`{{ ${value.trim()} }}`
    // Since the {{ }} delimiter is legacy, we'll just convert that style variable when possible.
    template: (value) => variableTemplate`@${value.trim()}`
  }
}

function resolveDelimiterConfig(delimiter) {
  if (typeof delimiter === "string") {
    return delimiterConfigsMap[delimiter]
  }
  return delimiter
}

/**
 * Resolve `[{ delimiters: ["at"], variables }]` into matchable groups
 * with concrete delimiter config objects.
 */
function resolveVariableGroups(variables = []) {
  return (variables || [])
    .map((group) => ({
      delimiterConfigs: (group.delimiters || [])
        .map(resolveDelimiterConfig)
        .filter(Boolean),
      variables: group.variables || {}
    }))
    .filter((group) => group.delimiterConfigs.length > 0)
}

const useVariableSelector = ({
  variables = [],
  value: valueProp,
  EmptyVariableSelectorList = InvalidVariable,
  /** Optional: (baseContext) => context passed to leaf.execute */
  createExecuteContext
}) => {
  const [value, setValue] = useState(valueProp || "")
  const [nextSelectionStart, setNextSelectionStart] = useState(0)
  const selectionStartRef = useRef(null)
  const [showVariableSelector, setShowVariableSelector] = useState(false)
  const [partialVariableMatch, setPartialVariableMatch] = useState(null)
  const [fullVariableMatch, setFullVariableMatch] = useState(null)
  const [activeVariableIndex, setActiveVariableIndex] = useState(0)
  const [selectorRect, setSelectorRect] = useState(null)
  // Variables tree for the currently matched delimiter group
  const [activeVariables, setActiveVariables] = useState({})
  // "down" or "up" – whether moving down or up in the picker via holding down the down/up arrow keys.
  const [keyDownNavigationMode, setKeyDownNavigationMode] = useState(null)
  const activeDelimiterConfigRef = useRef(null)

  const variableGroups = useMemo(
    () => resolveVariableGroups(variables),
    [variables]
  )

  // Flat list of { delimiterConfig, variables } for matching (first match wins)
  const matchEntries = useMemo(() => {
    const entries = []
    for (const group of variableGroups) {
      for (const delimiterConfig of group.delimiterConfigs) {
        entries.push({
          delimiterConfig,
          variables: group.variables
        })
      }
    }
    return entries
  }, [variableGroups])

  const closeSelector = useCallback(() => {
    setPartialVariableMatch(null)
    setFullVariableMatch(null)
    setActiveVariableIndex(0)
    setShowVariableSelector(false)
    setActiveVariables({})
    activeDelimiterConfigRef.current = null
  }, [])

  // filters out variables that don't match the partialVariableMatch + adds meta data like `index`
  const getFilteredVariables = useCallback(
    (variablesTree, path = "", fullLabel = "", totalIndeces = 0) => {
      const keys = Object.keys(variablesTree)

      for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        const variablePath = path ? `${path}.${key}` : key
        const variableFullPath = fullLabel
          ? `${fullLabel} ${variablesTree[key].label}`
          : variablesTree[key].label

        let childVariables = {}
        if (variablesTree[key].variables) {
          const [_childVariables, _totalIndeces] = getFilteredVariables(
            variablesTree[key].variables,
            variablePath,
            variableFullPath,
            totalIndeces
          )

          childVariables = _childVariables
          totalIndeces = _totalIndeces
        }

        if (
          // prettier-ignore
          partialVariableMatch &&
          variablePath.toLowerCase().indexOf(partialVariableMatch.toLowerCase()) === -1 &&
          variableFullPath.toLowerCase().indexOf(partialVariableMatch.toLowerCase()) === -1 &&
          Object.keys(childVariables).length === 0
        ) {
          delete variablesTree[key]
        } else {
          variablesTree[key].path = variablePath
          if (!variablesTree[key].variables) {
            variablesTree[key].index = totalIndeces
            totalIndeces++
          }
        }
      }

      return [variablesTree, totalIndeces]
    },
    [partialVariableMatch]
  )

  // returns the variable matching all the provided key/value pairs if found
  // as well as the variable's tree (tree can later be used to derive something, like the variable's full label)
  const getVariable = useCallback(
    (variablesTree, uniqueKeyValuePairs, tree = []) => {
      const keys = Object.keys(variablesTree)

      for (let i = 0; i < keys.length; i++) {
        const key = keys[i]

        tree.push(variablesTree[key])
        if (variablesTree[key].variables) {
          const [variable, nextTree] = getVariable(
            variablesTree[key].variables,
            uniqueKeyValuePairs,
            tree
          )
          if (variable) return [variable, nextTree]
          if (nextTree) nextTree.pop()
        } else if (
          // match all the provided key/value pairs
          Object.entries(uniqueKeyValuePairs).every(
            ([uniqueKey, uniqueValue]) =>
              variablesTree[key][uniqueKey] === uniqueValue
          )
        ) {
          return [variablesTree[key], tree]
        }
        tree.pop()
      }
      return [null, null]
    },
    []
  )

  // get the filteredVariables and totalIndeces based on the partialVariableMatch
  const [filteredVariables, totalIndeces] = useMemo(
    () => getFilteredVariables(cloneDeep(activeVariables)),
    [activeVariables, partialVariableMatch, getFilteredVariables]
  )

  useEffect(() => {
    if (
      Object.keys(filteredVariables).length === 0 &&
      !EmptyVariableSelectorList
    )
      setShowVariableSelector(false)

    // reset activeVariableIndex if the filteredVariables no longer contain a variable at the current activeVariableIndex
    // this could be due to user moving between variable delimeters
    if (!getVariable(filteredVariables, { index: activeVariableIndex })[0]) {
      setActiveVariableIndex(0)
    }
  }, [filteredVariables])

  // send value downstream
  useEffect(() => {
    if (valueProp !== value) setValue(valueProp)
  }, [valueProp])

  useEffect(() => {
    const variable =
      // prettier-ignore
      // attempt to get the variable based on path and index
      getVariable(filteredVariables, { path: fullVariableMatch, index: activeVariableIndex })[0] ||
      // attempt to get the variable based on path
      getVariable(filteredVariables, { path: fullVariableMatch })[0] ||
      // attempt to get the variable based on partial path
      // NOTE: the partial variable match lookup would likely never result in a matched variable
      getVariable(filteredVariables, { path: partialVariableMatch })[0]

    if (variable) {
      setActiveVariableIndex(variable.index)
    }
  }, [partialVariableMatch, fullVariableMatch])

  // reset after selector no longer shown
  useEffect(() => {
    if (!showVariableSelector) {
      setPartialVariableMatch(null)
      setFullVariableMatch(null)
      setActiveVariableIndex(0)
      setActiveVariables({})
      activeDelimiterConfigRef.current = null
    }
  }, [showVariableSelector])

  const handleSelection = useCallback(
    (e, val, nextSelectionStart) => {
      if (typeof val === "undefined") {
        val = e?.target.value
        nextSelectionStart = e?.target.selectionStart
      }

      // Catch unknown case where we still don't have a valid value.
      if (typeof val !== "string") return

      if (nextSelectionStart !== selectionStartRef.current)
        selectionStartRef.current = nextSelectionStart

      if (nextSelectionStart !== null) {
        const valueBeforeSelection = val.slice(0, nextSelectionStart)
        const valueAfterSelection = val.slice(nextSelectionStart)

        for (let i = 0; i < matchEntries.length; i++) {
          const { delimiterConfig, variables: groupVariables } = matchEntries[i]
          const { beforeSelectionRe, afterSelectionRe } = delimiterConfig
          if (beforeSelectionRe.test(valueBeforeSelection)) {
            // only handle setting the current value if we have a match, reduces unnecessary renders due to state change
            if (val !== value) setValue(val)
            if (!showVariableSelector) setShowVariableSelector(true)

            setActiveVariables(groupVariables)
            activeDelimiterConfigRef.current = delimiterConfig

            const partialVariableStart = valueBeforeSelection
              .match(beforeSelectionRe)
              .groups.partial.trim()
            setPartialVariableMatch(partialVariableStart)

            if (afterSelectionRe.test(valueAfterSelection)) {
              const partialVariableEnd = valueAfterSelection
                .match(afterSelectionRe)
                .groups.partial.trim()
              const fullVariable = `${partialVariableStart}${partialVariableEnd}`
              setFullVariableMatch(fullVariable)
            }
            // Early return, handling the first config that matches.
            return
          }
        }

        // No matching config, close variable selector if open.
        if (showVariableSelector) setShowVariableSelector(false)
      }
    },
    [showVariableSelector, value, matchEntries]
  )

  // Compute trigger match range for the active delimiter around the caret.
  const getTriggerMatch = useCallback(
    (variable) => {
      // substring of value starting at beginning of value, ending at current selection index
      const valueBeforeSelection = value.slice(0, selectionStartRef.current)
      // substring of value starting at current selection index, ending at end of value
      const valueAfterSelection = value.slice(selectionStartRef.current)

      const delimiterConfig =
        activeDelimiterConfigRef.current ||
        matchEntries.find(({ delimiterConfig: config }) =>
          config.beforeSelectionRe.test(valueBeforeSelection)
        )?.delimiterConfig

      if (!delimiterConfig) return null

      const { beforeSelectionRe, afterSelectionRe, template } = delimiterConfig
      if (!beforeSelectionRe.test(valueBeforeSelection)) return null

      const beforeMatch = valueBeforeSelection.match(beforeSelectionRe)
      const startIndex = beforeMatch.index
      const matchedStr = beforeMatch.groups.full

      let endIndex = startIndex + matchedStr.length

      if (afterSelectionRe.test(valueAfterSelection)) {
        const afterMatch = valueAfterSelection.match(afterSelectionRe)
        endIndex += afterMatch.groups.full.length
      } else if (variable) {
        // get the slice of the value starting at the beginning of the variable match
        const valueSubstr = value.slice(
          startIndex +
            beforeMatch.groups.full.length -
            beforeMatch.groups.partial.length
        )
        // attempt to get the endIndex by constucting RegExp from labels in variable tree
        // TODO: a complete solution would check all filteredVariables
        const [_, tree] = getVariable(filteredVariables, {
          path: variable.path
        })

        if (tree) {
          // NOTE: wrapping chars with [] to force literal matching (lazy way of escaping the . in variable path, for example)
          const constructReString = (words) => {
            const reStr = []
              .concat(words)
              .map((word, wi, words) => {
                let subReStr = word
                  .split("") // split all characters in word
                  .map((char, ci, chars) => {
                    // NOTE: lookbehinds aren't currently supported by all major browsers (looking at you Safari), so must resort to less specific re
                    if (ci || wi) return `[${char}]?` // optionally match char
                    return `[${char}]` // no preceeding char or preceeding word, at leas match beginning char
                  })
                  .join("")
                if (wi < words.length - 1) {
                  subReStr = `${subReStr}(?: +(?= *[${words[wi + 1][0]}]))?` // only match space(s) in between words if space(s) is/are followed by first char of next word (don't greedily grab those spaces)
                }
                return subReStr
              })
              .join("")

            // force match to start at beginning of string, ignoring spaces (necessary for if initial char of valueSubstr doesn't match the variable label or path)
            return `^ *${reStr}`
          }

          // construct RegExp string from variable paths and labels, separately
          const variablePathReString = constructReString(
            tree[tree.length - 1].path // only need the final path
          )
          const variableLabelReString = constructReString(
            tree.reduce(
              (words, { label }) => [
                ...words,
                ...label.toLowerCase().split(" ") // lowercase label and split into separate words
              ],
              []
            )
          )

          // now match either regexp, prioritizing the one that matches the most characters
          const afterMatch = [
            new RegExp(variablePathReString, "i"),
            new RegExp(variableLabelReString, "i")
          ]
            .map((re) => valueSubstr.match(re))
            .sort(
              (matchA, matchB) =>
                // sort match with most matched chars to the front
                (matchB ? matchB[0].length : 0) -
                (matchA ? matchA[0].length : 0)
            )[0]

          if (afterMatch) {
            const matchedAfterStr = afterMatch[0]
            // offset endIndex by the matchStr index & length, subtracting any variable match length
            endIndex +=
              afterMatch.index +
              matchedAfterStr.length -
              beforeMatch.groups.partial.length
          }
        }
      }

      return {
        startIndex,
        endIndex,
        template,
        delimiterConfig
      }
    },
    [value, filteredVariables, matchEntries, getVariable]
  )

  // selecting variable via clicking variable item or hitting enter
  const handleSelectVariable = useCallback(
    (variable) => {
      if (!variable) return

      const triggerMatch = getTriggerMatch(variable)
      if (!triggerMatch) return

      const { startIndex, endIndex, template } = triggerMatch

      // Action leaves (e.g. slash commands): skip text template insertion.
      if (typeof variable.execute === "function") {
        const deleteTrigger = () => {
          const nextValue = `${value.slice(0, startIndex)}${value.slice(
            endIndex
          )}`
          setValue(nextValue)
          setNextSelectionStart(startIndex)
        }

        const baseContext = {
          value,
          selectionStart: selectionStartRef.current,
          triggerRange: { startIndex, endIndex },
          deleteTrigger,
          close: closeSelector,
          variable
        }

        const context = createExecuteContext
          ? createExecuteContext(baseContext)
          : baseContext

        variable.execute(context)
        closeSelector()
        return
      }

      // format the result
      const variableString = template(variable.path)
      // get the value substsrings before and after the matched variable substring
      const beforeVariableString = value.slice(0, startIndex)
      const afterVariableString = value.slice(endIndex)
      // now insert the variableString
      const nextValue = `${beforeVariableString}${variableString}${afterVariableString}`
      // adjust the start of the selection to be at the end of the inserted variableString
      const nextSelStart = beforeVariableString.length + variableString.length
      // component using the hook will be updated with the value
      setValue(nextValue)
      setNextSelectionStart(nextSelStart)
      closeSelector()
    },
    [value, getTriggerMatch, closeSelector, createExecuteContext]
  )

  const handleKeyDown = useCallback(
    (e) => {
      const canNavigate = showVariableSelector && totalIndeces > 0
      if (!canNavigate) return

      const key = e.key
      const isDown = key === "ArrowDown" || e.keyCode === 40
      const isUp = key === "ArrowUp" || e.keyCode === 38
      const isEnter = key === "Enter" || e.keyCode === 13

      // Prevent caret jump / form submit while the picker owns up/down/enter.
      if (isDown) {
        setKeyDownNavigationMode("down")
      }
      if (isUp) {
        setKeyDownNavigationMode("up")
      }

      if (isDown || isUp || isEnter) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
    },
    [showVariableSelector, totalIndeces]
  )

  const handleNavigation = useCallback(
    (direction) => {
      if (direction === "down") {
        setActiveVariableIndex((index) => Math.min(index + 1, totalIndeces - 1))
      } else {
        setActiveVariableIndex((index) => Math.max(index - 1, 0))
      }
    },
    [totalIndeces]
  )

  const handleKeyUp = useCallback(
    (e, value, selectionStart) => {
      const canNavigate = showVariableSelector && totalIndeces > 0
      const key = e.key
      const isDown = key === "ArrowDown" || e.keyCode === 40
      const isUp = key === "ArrowUp" || e.keyCode === 38
      const isEnter = key === "Enter" || e.keyCode === 13
      const isEscape = key === "Escape" || e.keyCode === 27

      if (keyDownNavigationMode) {
        setKeyDownNavigationMode(null)
      }

      if (canNavigate && (isDown || isUp || isEnter)) {
        e.preventDefault()
        e.stopPropagation()

        if (isDown) {
          handleNavigation("down")
          return
        }
        if (isUp) {
          handleNavigation("up")
          return
        }
        if (isEnter) {
          const [selectedVariable] = getVariable(filteredVariables, {
            index: activeVariableIndex
          })
          if (selectedVariable) handleSelectVariable(selectedVariable)
          return
        }
      }

      if (isEscape) {
        // close the selector when user hits escape key
        if (showVariableSelector) setShowVariableSelector(false)
        // ignore when user hits escape key
        return
      }

      handleSelection(e, value, selectionStart)
    },
    [
      showVariableSelector,
      handleSelectVariable,
      activeVariableIndex,
      totalIndeces,
      filteredVariables,
      getVariable,
      handleSelection,
      handleNavigation,
      keyDownNavigationMode
    ]
  )

  useEffect(() => {
    if (keyDownNavigationMode) {
      const timeout = setTimeout(() => {
        handleNavigation(keyDownNavigationMode)
      }, 100)
      return () => clearTimeout(timeout)
    }
  }, [handleNavigation, keyDownNavigationMode, activeVariableIndex])

  const handleChange = useCallback(
    (e, val) => {
      if (typeof val !== "undefined") {
        if (val !== value) setValue(val)
      } else {
        if (e.target.value !== value) setValue(e.target.value)
      }
    },
    [value]
  )

  const handleBlur = useCallback(
    (e) => {
      if (showVariableSelector) {
        // HACK ALERT: fix for when the blur is occurring because user is clicking the variable selector UI, in which case we don't want to close the variable selecor just yet
        setTimeout(() => {
          setShowVariableSelector(false)
        }, 150)
      }
    },
    [showVariableSelector]
  )

  // Choose to ignore the partial variable match when there are spaces or
  // ends in a period and no exact matches are found.
  // Exact text-variable matches hide the picker (typed path is already complete).
  // Exact execute-command matches keep the picker open so Enter/click can run the action.
  const ignoreVariable = useMemo(() => {
    const [exactMatch] = getVariable(filteredVariables, {
      path: partialVariableMatch
    })
    const hasExactMatch = !!exactMatch
    const exactMatchIsCommand = typeof exactMatch?.execute === "function"
    const hasPartialMatch = !!Object.keys(filteredVariables).length
    const hasSpaces = /\.? +/.test(partialVariableMatch)
    const endsInPerioud = /\.$/.test(partialVariableMatch)
    return (
      ((hasSpaces || endsInPerioud) && !hasPartialMatch) ||
      (hasExactMatch && !exactMatchIsCommand)
    )
  }, [partialVariableMatch, filteredVariables])

  const selector =
    showVariableSelector && !ignoreVariable ? (
      <VariableSelectorPicker rect={selectorRect}>
        <VariableSelectorList
          variable={partialVariableMatch}
          variables={filteredVariables}
          activeVariableIndex={activeVariableIndex}
          handleSelectVariable={handleSelectVariable}
          EmptyVariableSelectorList={EmptyVariableSelectorList}
        />
      </VariableSelectorPicker>
    ) : null

  return {
    selector,
    showingVariableSelector: showVariableSelector,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    handleSelection: handleSelection,
    onChange: handleChange,
    value: value,
    nextSelectionStart,
    setSelectorRect: setSelectorRect
  }
}

export default useVariableSelector
